import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  DialogTitle,
} from '@tinct/web-components';
import {
  AudienceLeadsImportState,
  ImportResponse,
  ReadCampaignRef,
} from '@tinct/core-api-models';
import {CircleCheckIcon, InfoIcon} from 'lucide-react';
import {useStaticAssetsUrl} from '@tinct/core-utils';
import {useGetJobById} from '@tinct/core-api';

export type SuccessStepProps = {
  audienceName: string;
  importResult: ImportResponse;
  onDone: () => void;
  campaigns?: ReadCampaignRef[];
  campaignBaseUrl?: string;
};

const audienceLeadsImportState = (
  name: string | null | undefined,
  state: unknown,
): AudienceLeadsImportState | null => {
  if (name !== 'audience_leads_import') return null;
  if (!state || typeof state !== 'object') return null;
  return state as AudienceLeadsImportState;
};

export const SuccessStep = ({
  audienceName,
  importResult,
  onDone,
  campaigns,
  campaignBaseUrl,
}: SuccessStepProps) => {
  const assetsUrl = useStaticAssetsUrl();
  const jobId = importResult.jobId ?? null;

  const {data: job} = useGetJobById(
    {
      id: jobId ?? '',
      fields: {
        id: null,
        name: null,
        state: null,
      },
    },
    {enabled: !!jobId},
  );

  const leadsState = audienceLeadsImportState(job?.name, job?.state);
  const totalCompanies =
    leadsState?.totalEnrichedLeads ?? importResult.successCount ?? 0;
  const errors = importResult.errorCount ?? 0;
  const hasCampaigns = campaigns && campaigns.length > 0;

  return (
    <div className="relative overflow-clip rounded-3xl border bg-background shadow-lg">
      {/* Cover image */}
      <div className="pt-2 px-2">
        <div className="overflow-clip rounded-2xl bg-indigo-200">
          <img
            src={`${assetsUrl}/import-done.png`}
            alt="Audience ready"
            className="w-full object-cover aspect-[2/1]"
          />
        </div>
      </div>

      {/* Head */}
      <div className="p-8 text-center">
        <DialogTitle className="text-2xl font-semibold tracking-[-0.4px]">
          Your audience is ready!
        </DialogTitle>
        <p className="text-base text-muted-foreground mt-3">
          {hasCampaigns
            ? 'To generate the page variants, go to the campaign\'s dashboard'
            : 'Let\'s generate your page variants!'}
        </p>
      </div>

      {/* Alerts */}
      <div className="flex flex-col gap-2 px-8 pb-8">
        <div className="flex items-center gap-4 bg-teal-50 border border-teal-100 rounded-2xl p-4">
          <CircleCheckIcon className="size-4 shrink-0 text-teal-600" />
          <p className="text-sm font-medium text-teal-600 truncate">
            {audienceName} created with {totalCompanies} companies.
          </p>
        </div>

        {errors > 0 && (
          <Alert className="rounded-2xl">
            <InfoIcon className="size-4" />
            <AlertTitle>Info</AlertTitle>
            <AlertDescription>
              <ul className="list-disc ms-[21px]">
                <li>{errors} companies ignored : duplicates</li>
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {hasCampaigns && (
          <Alert className="rounded-2xl border-indigo-100 bg-indigo-50 text-indigo-600">
            <InfoIcon className="size-4" />
            <AlertTitle>Linked campaigns</AlertTitle>
            <AlertDescription>
              <ul className="list-disc ms-[21px]">
                {campaigns.map((c) => (
                  <li key={c.id}>
                    <a
                      href={`${campaignBaseUrl}/${c.id}`}
                      className="text-primary hover:underline"
                    >
                      {c.name}
                    </a>
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Footer */}
      <div className="px-8 pb-8">
        <Button onClick={onDone} size="lg" className="w-full">
          Done !
        </Button>
      </div>
    </div>
  );
};